import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import API from 'api';
import { imagesURL } from 'config';
import classes from './AddAudio.module.scss';

const normalize = (value) => String(value || '').trim().toLowerCase().replace(/_/g, '-');

const isHaitianLocale = (locale) => {
  if (!locale) return false;
  if (locale?.ttsConfig?.haitianCreole?.enable === false) return false;

  const code = normalize(locale?.code);
  const base = code.split('-')[0];
  if (base === 'ht' || base === 'hc') return true;

  const label = normalize(`${locale?.name || ''} ${locale?.displayName || ''}`);
  return label.includes('haitian') || label.includes('creole');
};

const getVoiceId = (voice) => String(voice?.id || voice?.voiceId || voice?.voice_id || voice?.name || '').trim();
const getVoiceGender = (voice) => String(voice?.gender || '').trim().toLowerCase();
const getVoiceAccent = (voice) => String(voice?.accent || voice?.region || '').trim();
const getVoiceLabel = (voice) => {
  const name = String(voice?.name || voice?.displayName || voice?.display_name || getVoiceId(voice)).trim();
  const details = [getVoiceGender(voice), getVoiceAccent(voice)].filter(Boolean);
  return details.length ? `${name} (${details.join(', ')})` : name;
};

const getSelectionStorageKey = (localeCode, provider) => (
  `flc.haitianTtsSelection.${normalize(localeCode)}.${normalize(provider) || 'default'}`
);

const readSavedSelection = (localeCode, provider) => {
  if (typeof window === 'undefined') return null;
  try {
    const storage = window.localStorage;
    if (!storage) return null;
    const raw = storage.getItem(getSelectionStorageKey(localeCode, provider));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      voiceId: String(parsed?.voiceId || '').trim(),
      accent: String(parsed?.accent || '').trim(),
    };
  } catch {
    return null;
  }
};

const saveSelection = (localeCode, provider, selection) => {
  if (typeof window === 'undefined' || !localeCode) return;
  try {
    const storage = window.localStorage;
    if (!storage) return;
    storage.setItem(getSelectionStorageKey(localeCode, provider), JSON.stringify({
      voiceId: String(selection?.voiceId || '').trim(),
      accent: String(selection?.accent || '').trim(),
    }));
  } catch {
    // Ignore storage failures; TTS selection still works for the current session.
  }
};

const getProviderLabel = (provider) => {
  if (provider === 'creolecentric') return 'CreoleCentric';
  if (provider === 'yourvoic') return 'YourVoic AI';
  return provider || 'Haitian Creole TTS';
};

const HaitianTtsControls = ({ locale, associatedText, disabledButton, defaultVoice, onGenerate }) => {
  const available = isHaitianLocale(locale);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('');
  const [voices, setVoices] = useState([]);
  const [accents, setAccents] = useState([]);
  const [voiceId, setVoiceId] = useState('');
  const [accent, setAccent] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    if (!available || !locale?.code) {
      setLoading(false);
      setProvider('');
      setVoices([]);
      setAccents([]);
      setVoiceId('');
      setAccent('');
      setError('');
      return () => { active = false; };
    }

    setLoading(true);
    setError('');

    API.polly.getHaitianTtsOptions(locale.code).then((result = {}) => {
      if (!active) return;

      const nextVoices = Array.isArray(result.voices) ? result.voices.filter((voice) => getVoiceId(voice)) : [];
      const nextAccents = Array.isArray(result.accents) ? result.accents : [];
      const nextProvider = result.provider || locale?.ttsConfig?.haitianCreole?.provider || '';
      const savedSelection = readSavedSelection(locale.code, nextProvider);
      const savedAccent = nextAccents.some((item) => String(item?.value || '') === savedSelection?.accent)
        ? savedSelection.accent
        : '';
      const voicesForSavedAccent = savedAccent
        ? nextVoices.filter((voice) => {
          const voiceAccent = normalize(getVoiceAccent(voice));
          return !voiceAccent || voiceAccent === normalize(savedAccent);
        })
        : nextVoices;
      const savedVoice = voicesForSavedAccent.find((voice) => getVoiceId(voice) === savedSelection?.voiceId) || null;
      const preferredGender = String(defaultVoice || '').toLowerCase();
      const preferredVoice = savedVoice
        || voicesForSavedAccent.find((voice) => getVoiceGender(voice) === preferredGender)
        || voicesForSavedAccent[0]
        || nextVoices[0]
        || null;

      setProvider(nextProvider);
      setVoices(nextVoices);
      setAccents(nextAccents);
      setVoiceId(getVoiceId(preferredVoice));
      setAccent(savedAccent);
      setError(result.success === false ? (result.error || 'Unable to load Haitian TTS voices') : (result.error || ''));
      setLoading(false);
    });

    return () => { active = false; };
  }, [available, locale?.code, locale?.ttsConfig?.haitianCreole?.provider, defaultVoice]);

  const visibleVoices = useMemo(() => {
    if (!accent) return voices;
    const matching = voices.filter((voice) => {
      const voiceAccent = normalize(getVoiceAccent(voice));
      return !voiceAccent || voiceAccent === normalize(accent);
    });
    return matching.length ? matching : voices;
  }, [voices, accent]);

  useEffect(() => {
    if (!visibleVoices.length) {
      if (voiceId) setVoiceId('');
      return;
    }
    if (!visibleVoices.some((voice) => getVoiceId(voice) === voiceId)) {
      setVoiceId(getVoiceId(visibleVoices[0]));
    }
  }, [visibleVoices, voiceId]);

  if (!available) return null;

  const handleVoiceChange = (event) => {
    const nextVoiceId = event.target.value;
    setVoiceId(nextVoiceId);
    saveSelection(locale?.code, provider, { voiceId: nextVoiceId, accent });
  };

  const handleAccentChange = (event) => {
    const nextAccent = event.target.value;
    const matchingVoices = nextAccent
      ? voices.filter((voice) => {
        const voiceAccent = normalize(getVoiceAccent(voice));
        return !voiceAccent || voiceAccent === normalize(nextAccent);
      })
      : voices;
    const nextVoiceId = matchingVoices.some((voice) => getVoiceId(voice) === voiceId)
      ? voiceId
      : getVoiceId(matchingVoices[0]);

    setAccent(nextAccent);
    setVoiceId(nextVoiceId);
    saveSelection(locale?.code, provider, { voiceId: nextVoiceId, accent: nextAccent });
  };

  const selectedVoice = voices.find((voice) => getVoiceId(voice) === voiceId) || null;
  const voiceGender = getVoiceGender(selectedVoice) || String(defaultVoice || 'female').toLowerCase() || 'female';
  const canGenerate = !!associatedText && !!locale?.code && !!voiceId && !disabledButton && !loading;

  return (
    <div className={classes.row}>
      <button
        type="button"
        title="Generate Haitian Creole TTS"
        className={`${classes['tts-btn']} btn-effect`}
        onClick={() => onGenerate({
          locale: locale.code,
          voice: voiceGender === 'male' ? 'male' : 'female',
          haitianVoiceId: voiceId,
          haitianAccent: accent,
          haitianProvider: provider,
        })}
        disabled={!canGenerate}
      >
        <img src={`${imagesURL}/FlinkMake/TTS.png`} alt="Haitian Creole TTS" width={50} height={50} />
      </button>

      <div className={classes['tts-picker']}>
        <strong style={{ display: 'block', marginBottom: 4 }}>Haitian Creole TTS</strong>
        <div style={{ marginBottom: 6 }}>{getProviderLabel(provider)}</div>

        <label style={{ display: 'block', marginBottom: 6 }}>
          <span style={{ display: 'block', marginBottom: 2 }}>Voice</span>
          <select
            value={voiceId}
            onChange={handleVoiceChange}
            disabled={loading || !visibleVoices.length}
          >
            {!visibleVoices.length && <option value="">{loading ? 'Loading voices...' : 'No voices available'}</option>}
            {visibleVoices.map((voice) => (
              <option key={`haitian-voice-${getVoiceId(voice)}`} value={getVoiceId(voice)}>
                {getVoiceLabel(voice)}
              </option>
            ))}
          </select>
        </label>

        {!!accents.length && (
          <label style={{ display: 'block', marginBottom: 6 }}>
            <span style={{ display: 'block', marginBottom: 2 }}>Accent</span>
            <select value={accent} onChange={handleAccentChange} disabled={loading}>
              {!accents.some((item) => String(item?.value || '') === '') && <option value="">Provider default</option>}
              {accents.map((item) => (
                <option key={`haitian-accent-${String(item?.value || 'default')}`} value={String(item?.value || '')}>
                  {item?.label || item?.value || 'Provider default'}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && <div style={{ color: '#b00020', maxWidth: 360 }}>{error}</div>}
      </div>
    </div>
  );
};

HaitianTtsControls.propTypes = {
  locale: PropTypes.object,
  associatedText: PropTypes.string,
  disabledButton: PropTypes.bool,
  defaultVoice: PropTypes.string,
  onGenerate: PropTypes.func.isRequired,
};

export default HaitianTtsControls;
